/**
 * Helpers for SampleManager component
 *
 * - emptyRow: template for empty sample row
 * - generateWellPosition: generate well position from index
 * - createEmptyRows: creates array of empty sample rows
 * - toCamelCase: simple camelCase converter for header labels
 * - extractFile: pulls a File/Blob/string out of various upload component payloads
 * - REQUIRED_HEADERS: constant list of expected headers (human-friendly labels)
 * - SAMPLE_COUNT_OPTIONS: allowed sample count options
 * - findNearestSampleCount: picks nearest allowed sample count for a given actual count
 * - createSampleTemplate: builds a CSV Blob and a suggested filename for client-side download
 */
import { PLACEHOLDER_VALUE } from '@/constants';

/**
 * Empty row template
 */
export const emptyRow = {
  study_name: PLACEHOLDER_VALUE,
  org_name: PLACEHOLDER_VALUE,
};

/**
 * Generate well position from index
 */
export const generateWellPosition = (i: number) => Math.ceil((i - 1) / 2 + 1);

/**
 * Creates an array of empty sample rows of the specified count.
 */
export function createEmptyRows(count?: number) {
  const n = count ?? 12;
  return Array.from({ length: n }, (_, i) => {
    let demux_index: number;
    let well_position: string;

    if (n > 24) {
      demux_index = (i % 3) + 1;
      well_position = `A${Math.floor(i / 3) + 1}`;
    } else if (n > 12) {
      demux_index = (i % 2) + 1;
      well_position = `A${generateWellPosition(i)}`;
    } else {
      demux_index = 1;
      well_position = `A${i + 1}`;
    }

    return {
      ...emptyRow,
      demux_index,
      name: `Sample ${i + 1}`,
      well_position,
    };
  });
}

/**
 * Helper function to convert a string to camelCase
 * @param str - The string to convert
 * @returns The camelCase version of the string
 * @example toCamelCase('Name') // 'name'
 * @example toCamelCase('wellPosition') // 'wellPosition'
 */
export const toCamelCase = (str: string): string => str.charAt(0).toLowerCase() + str.slice(1);

/**
 * Extract file from various input formats that might be returned by file upload components
 * @param fileData - The data from the file upload component (could be File, FileList, Blob, string, or array)
 * @returns The extracted File, Blob, string, or null if invalid
 */
export const extractFile = (fileData: unknown): File | Blob | string | null => {
  if (typeof fileData === 'string') {
    return fileData;
  }

  // Handle FileList (most common from file input elements)
  if (fileData && typeof fileData === 'object' && 'length' in fileData && '0' in fileData) {
    const firstItem = (fileData as unknown[])[0];
    if (firstItem instanceof File) {
      return firstItem;
    }
  }

  // Handle direct File or Blob
  if (fileData instanceof File || fileData instanceof Blob) {
    return fileData;
  }

  // Handle array of files
  if (Array.isArray(fileData) && fileData.length > 0) {
    const firstFile = fileData[0];
    if (typeof firstFile === 'string' || firstFile instanceof File || firstFile instanceof Blob) {
      return firstFile;
    }
  }

  return null;
};

/**
 * Required CSV headers for sample data (in human-friendly format)
 */
export const REQUIRED_HEADERS = [
  'Instrument Run',
  'Sample Name',
  'Sample Well Position',
  'Sample Demux Index',
  'Study',
  'Organization',
] as const;

/**
 * Available options for number of samples field
 */
export const SAMPLE_COUNT_OPTIONS = [4, 12, 24] as const;

/**
 * Find the nearest available sample count option
 * @param actualCount - The actual number of samples from the CSV
 * @returns The nearest available option from SAMPLE_COUNT_OPTIONS (string)
 */
export const findNearestSampleCount = (actualCount: number): string => {
  // Find the option with minimum distance to actualCount
  const nearest = SAMPLE_COUNT_OPTIONS.reduce((prev, curr) => {
    return Math.abs(curr - actualCount) < Math.abs(prev - actualCount) ? curr : prev;
  });
  return String(nearest);
};

/**
 * Create a CSV Blob (and filename) for the sample template.
 *
 * This keeps the CSV generation logic centralized so the component can simply
 * request the blob and trigger a client download.
 *
 * @param noOfSamples - number of sample rows to generate
 * @param selectedOrg - optional object with { id?: string; name?: string } to customize example values / filename
 * @param instrumentRunName - optional instrument run name from the form to pre-fill in the template
 * @param studyName - optional study name from the form to pre-fill in the template
 * @returns An object with the CSV Blob and a suggested filename
 */
const escapeCell = (cell: string) => {
  if (cell.includes(',') || cell.includes('"') || cell.includes('\n')) {
    return `"${cell.replaceAll('"', '""')}"`;
  }
  return cell;
};

export const createSampleTemplate = (
  noOfSamples: number,
  selectedOrg?: { id?: string; name?: string } | null,
  instrumentRunName?: string,
  studyName?: string,
): { blob: Blob; filename: string } => {
  const headers = [
    'Instrument Run',
    'Sample Name',
    'Sample Well Position',
    'Sample Demux Index',
    'Study',
    'Organization',
  ];

  // Generate templated rows
  const rows = Array.from({ length: noOfSamples }, (_, i) => [
    instrumentRunName || '',
    `Sample ${i + 1}`,
    noOfSamples > 12 ? `A${noOfSamples > 24 ? Math.floor(i / 3) + 1 : generateWellPosition(i)}` : `A${i + 1}`,
    `${(i % (noOfSamples > 24 ? 3 : 2)) + 1}`,
    studyName || '',
    selectedOrg?.name || '',
  ]);

  const csvContent = [headers, ...rows].map(row => row.map(escapeCell).join(',')).join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const orgSuffix = selectedOrg?.id ? `_${selectedOrg.id}` : '';
  const filename = `sample_template${orgSuffix}.csv`;

  return { blob, filename };
};
